// Tema global para VRSimracers Manager
export const theme = {
  colors: {
    // Colores principales del logo
    primary: {
      main: '#101325',      // Azul corporativo
      light: '#1e2340',
      dark: '#0a0c19',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#e4312e',      // Rojo corporativo
      light: '#ff6259',
      dark: '#a90000',
      contrastText: '#FFFFFF'
    },
    // Colores complementarios
    background: {
      default: '#F5F7FA',   // Gris más oscuro para fondos (antes '')
      paper: '#FFFFFF',     // Blanco para tarjetas
      dark: '#101325'       // Azul corporativo para áreas destacadas
    },
    text: {
      primary: '#101325',   // Texto principal
      secondary: '#5A6178', // Texto secundario
      disabled: '#9DA3B4',  // Texto deshabilitado
      hint: '#9DA3B4'       // Texto de ayuda
    },
    divider: 'rgba(23, 26, 46, 0.12)',
    // Colores para podio/gaming
    gold: '#FFD700',
    silver: '#C0C0C0',
    bronze: '#CD7F32',
    // Estados
    success: {
      main: '#2E6AE4',      // Azul claro derivado del corporativo
      light: '#5A8AFF',
      dark: '#1A4DB0',
      contrastText: '#FFFFFF'
    },
    warning: {
      main: '#FF9500',      // Naranja para advertencias
      light: '#FFAA33',
      dark: '#E68600',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#e4312e',      // Rojo corporativo para errores
      light: '#ff6259',
      dark: '#a90000',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#101325',      // Azul corporativo para información
      light: '#1e2340',
      dark: '#0a0c19',
      contrastText: '#FFFFFF'
    }
  },
  // Sombras para diferentes elevaciones
  shadows: {
    small: '0 2px 8px rgba(23, 26, 46, 0.08)',
    medium: '0 4px 16px rgba(23, 26, 46, 0.12)',
    large: '0 8px 24px rgba(23, 26, 46, 0.16)'
  },
  // Bordes redondeados
  borderRadius: {
    small: '4px',
    medium: '8px',
    large: '16px',
    circle: '50%'
  },
  // Transiciones
  transitions: {
    fast: 'all 0.2s ease',
    normal: 'all 0.3s ease',
    slow: 'all 0.5s ease'
  },
  // Espaciado
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px'
  },
  // Tipografía
  typography: {
    fontFamily: "'Inter', 'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif",
    fontWeights: {
      light: 300,
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    },
    sizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      md: '1rem',       // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      xxl: '1.5rem',    // 24px
      h1: '2.25rem',    // 36px
      h2: '1.875rem',   // 30px
      h3: '1.5rem',     // 24px
      h4: '1.25rem',    // 20px
      h5: '1.125rem',   // 18px
      h6: '1rem'        // 16px
    }
  }
};

// Estilos comunes reutilizables
export const commonStyles = {
  // Tarjeta moderna con sombra suave
  card: `
    background-color: ${theme.colors.background.paper};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.small};
    transition: ${theme.transitions.normal};
    
    &:hover {
      box-shadow: ${theme.shadows.medium};
    }
  `,
  
  // Botón principal
  primaryButton: `
    background-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrastText};
    border: none;
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-weight: ${theme.typography.fontWeights.medium};
    transition: ${theme.transitions.fast};
    cursor: pointer;
    
    &:hover {
      background-color: ${theme.colors.primary.light};
    }
    
    &:active {
      background-color: ${theme.colors.primary.dark};
    }
    
    &:disabled {
      background-color: ${theme.colors.text.disabled};
      cursor: not-allowed;
    }
  `,
  
  // Botón secundario
  secondaryButton: `
    background-color: ${theme.colors.secondary.main};
    color: ${theme.colors.secondary.contrastText};
    border: none;
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    font-weight: ${theme.typography.fontWeights.medium};
    transition: ${theme.transitions.fast};
    cursor: pointer;
    
    &:hover {
      background-color: ${theme.colors.secondary.light};
    }
    
    &:active {
      background-color: ${theme.colors.secondary.dark};
    }
    
    &:disabled {
      background-color: ${theme.colors.text.disabled};
      cursor: not-allowed;
    }
  `,
  
  // Botón de texto
  textButton: `
    background-color: transparent;
    color: ${theme.colors.primary.main};
    border: none;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-weight: ${theme.typography.fontWeights.medium};
    transition: ${theme.transitions.fast};
    cursor: pointer;
    
    &:hover {
      background-color: rgba(23, 26, 46, 0.05);
    }
    
    &:active {
      background-color: rgba(23, 26, 46, 0.1);
    }
    
    &:disabled {
      color: ${theme.colors.text.disabled};
      cursor: not-allowed;
    }
  `,
  
  // Input moderno
  input: `
    width: 100%;
    padding: ${theme.spacing.md};
    border: 1px solid ${theme.colors.divider};
    border-radius: ${theme.borderRadius.small};
    font-size: ${theme.typography.sizes.md};
    transition: ${theme.transitions.fast};
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary.main};
      box-shadow: 0 0 0 2px rgba(23, 26, 46, 0.1);
    }
    
    &::placeholder {
      color: ${theme.colors.text.hint};
    }
    
    &:disabled {
      background-color: ${theme.colors.background.default};
      color: ${theme.colors.text.disabled};
      cursor: not-allowed;
    }
  `,
  
  // Tabla moderna
  table: `
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    
    th {
      background-color: ${theme.colors.background.default};
      color: ${theme.colors.text.secondary};
      font-weight: ${theme.typography.fontWeights.semibold};
      text-align: left;
      padding: ${theme.spacing.md};
      border-bottom: 1px solid ${theme.colors.divider};
    }
    
    td {
      padding: ${theme.spacing.md};
      border-bottom: 1px solid ${theme.colors.divider};
    }
    
    tr:last-child td {
      border-bottom: none;
    }
    
    tr:hover td {
      background-color: rgba(23, 26, 46, 0.02);
    }
  `,
  
  // Badge o etiqueta
  badge: `
    display: inline-block;
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    border-radius: ${theme.borderRadius.small};
    font-size: ${theme.typography.sizes.xs};
    font-weight: ${theme.typography.fontWeights.medium};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  `,
  
  // Contenedor de página
  pageContainer: `
    padding: ${theme.spacing.xl};
    max-width: 1200px;
    margin: 0 auto;
  `,
  
  // Título de sección
  sectionTitle: `
    color: ${theme.colors.text.primary};
    font-size: ${theme.typography.sizes.xxl};
    font-weight: ${theme.typography.fontWeights.bold};
    margin-bottom: ${theme.spacing.lg};
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -${theme.spacing.xs};
      left: 0;
      width: 40px;
      height: 3px;
      background-color: ${theme.colors.secondary.main};
    }
  `
};

export default theme;
